// SupervisionPOC.js — Documentation Fonctionnelle : POC « Supervision des Applications » (module Rapports & Supervision).
// Aperçu interactif de l'écran réel de supervision type NOC, fidèle à sa DISPOSITION :
//   - barre sombre de supervision (titre, KPIs globaux : apps / sessions / anomalies ouvertes /
//     score global, recherche, filtre environnement, auto-rafraîchissement) ;
//   - matrice domaines × modules : un « nœud application » par domaine ACTIF (jauge de score,
//     badges, KPIs) contenant les cartes de ses modules CBS (mini-jauge, sessions, pastilles
//     de sévérité, mini-barres par catégorie, dernière session) ;
//   - drill-down : clic sur un module → panneau de détail (bySeverity, byCategory, ouvertes) ;
//   - légende des seuils (vert ≥ 85, ambre ≥ 70, rouge < 70) et des catégories de contrôle.
// L'écran réel est un canevas SVG pan/zoom ; le POC restitue les nœuds en flux vertical.
// Graphiques = divs + conic-gradient « maison ». Actions SIMULÉES localement (aucun appel réseau).
// Source de vérité : scanbank-frontend/src/features/[SCANBANK] Reports/[RPT][03]-Supervision.js
// + agrégat serveur GET /api/scanbank/reports/supervision (SCANBANK_REPORT_VIEW).
import React, { useState } from 'react';
import {
  Monitor, Server, Database, Shield, RefreshCw, Search, Filter, Activity,
  AlertTriangle, AlertOctagon, CheckCircle, Award, Layers,
  BarChart3, ArrowRight, Info, X, Play, Pause, ListChecks, Calculator, Clock, Eye,
} from 'lucide-react';
import { Card, CardContent, Badge, EmptyState } from '../ui';

// ---- Seuils & couleurs de score (règle ScanBank : ACCEPTABLE ≥ 85, ALERTE ≥ 70, CRITIQUE sinon)
const scoreColor = (s) => (s == null ? '#9CA3AF' : s >= 85 ? '#10B981' : s >= 70 ? '#F59E0B' : '#EF4444');
const scoreLevel = (s) => (s == null ? '—' : s >= 85 ? 'ACCEPTABLE' : s >= 70 ? 'ALERTE' : 'CRITIQUE');

// ---- Dictionnaires (types système, environnements, sévérités, catégories) -------
const SYSTEM_TYPES = {
  CBS: { label: 'Core Banking', color: '#6366F1', icon: Database },
  ERP: { label: 'ERP', color: '#3B82F6', icon: Server },
  RISK_MANAGEMENT: { label: 'Gestion Risques', color: '#EF4444', icon: Shield },
  AML: { label: 'Anti-Blanchiment', color: '#F59E0B', icon: AlertOctagon },
  OTHER: { label: 'Autre', color: '#8B5CF6', icon: Server },
};

const ENVIRONNEMENTS = {
  PROD: { label: 'PROD', color: '#EF4444' },
  UAT: { label: 'UAT', color: '#F59E0B' },
  DEV: { label: 'DEV', color: '#3B82F6' },
  PREPROD: { label: 'PREPROD', color: '#F97316' },
};

const SEVERITES = {
  CRITICAL: { label: 'Critique', color: '#EF4444', dot: 'bg-red-500' },
  MAJOR: { label: 'Majeure', color: '#F59E0B', dot: 'bg-amber-500' },
  MINOR: { label: 'Mineure', color: '#60A5FA', dot: 'bg-blue-400' },
};

// Catégories de contrôle (mêmes codes courts que l'écran réel : SEC/CMP/COH/PRF/QUA)
const CATEGORIES = {
  SECURITY: { label: 'Sécurité', short: 'SEC', color: '#EF4444', bg: 'bg-red-500' },
  COMPLIANCE: { label: 'Conformité', short: 'CMP', color: '#F59E0B', bg: 'bg-amber-500' },
  CONSISTENCY: { label: 'Cohérence', short: 'COH', color: '#3B82F6', bg: 'bg-blue-500' },
  PERFORMANCE: { label: 'Performance', short: 'PRF', color: '#8B5CF6', bg: 'bg-purple-500' },
  QUALITY: { label: 'Qualité', short: 'QUA', color: '#10B981', bg: 'bg-emerald-500' },
};

// ---- Données en dur : réponse simulée de GET /api/scanbank/reports/supervision ---
// Un élément par domaine ACTIF, avec le détail agrégé par module CBS. Cohérent avec les
// POC Dashboard / SessionReports / DomainDashboard (mêmes domaines, scores, volumes).
const SUPERVISION = [
  {
    code: 'CBS_AMPLITUDE', name: 'Core Banking Amplitude', systemType: 'CBS', environment: 'PROD',
    version: '11.2', active: true, scoreMoyen: 81.7,
    modules: [
      { module: 'HABILITATION', totalSessions: 6, scoreMoyen: 68.4, derniereSession: '08/07/2026 09:34',
        anomalies: { bySeverity: { CRITICAL: 5, MAJOR: 9, MINOR: 12 }, byCategory: { SECURITY: 14, COMPLIANCE: 6, CONSISTENCY: 3, QUALITY: 3 }, ouvertes: 7 } },
      { module: 'COMPTABILITE', totalSessions: 5, scoreMoyen: 90.2, derniereSession: '10/07/2026 14:16',
        anomalies: { bySeverity: { CRITICAL: 0, MAJOR: 3, MINOR: 8 }, byCategory: { COMPLIANCE: 5, CONSISTENCY: 4, QUALITY: 2 }, ouvertes: 2 } },
      { module: 'CREDITS', totalSessions: 5, scoreMoyen: 77.1, derniereSession: '11/07/2026 08:19',
        anomalies: { bySeverity: { CRITICAL: 2, MAJOR: 6, MINOR: 7 }, byCategory: { COMPLIANCE: 7, CONSISTENCY: 5, QUALITY: 2, SECURITY: 1 }, ouvertes: 5 } },
      { module: 'DEPOTS', totalSessions: 4, scoreMoyen: 88.0, derniereSession: '09/07/2026 07:45',
        anomalies: { bySeverity: { CRITICAL: 0, MAJOR: 2, MINOR: 5 }, byCategory: { CONSISTENCY: 4, QUALITY: 3 }, ouvertes: 1 } },
      { module: 'VIREMENTS', totalSessions: 6, scoreMoyen: 82.6, derniereSession: '11/07/2026 15:42',
        anomalies: { bySeverity: { CRITICAL: 1, MAJOR: 4, MINOR: 6 }, byCategory: { SECURITY: 3, COMPLIANCE: 4, PERFORMANCE: 2, CONSISTENCY: 2 }, ouvertes: 4 } },
      { module: 'CLIENTS', totalSessions: 4, scoreMoyen: 86.9, derniereSession: '07/07/2026 11:20',
        anomalies: { bySeverity: { CRITICAL: 0, MAJOR: 3, MINOR: 4 }, byCategory: { COMPLIANCE: 4, QUALITY: 3 }, ouvertes: 2 } },
      { module: 'TRESORERIE', totalSessions: 3, scoreMoyen: 91.4, derniereSession: '06/07/2026 08:12',
        anomalies: { bySeverity: { CRITICAL: 0, MAJOR: 1, MINOR: 3 }, byCategory: { PERFORMANCE: 2, CONSISTENCY: 2 }, ouvertes: 1 } },
      { module: 'RISQUES', totalSessions: 3, scoreMoyen: 74.8, derniereSession: '05/07/2026 09:00',
        anomalies: { bySeverity: { CRITICAL: 1, MAJOR: 3, MINOR: 2 }, byCategory: { COMPLIANCE: 4, SECURITY: 2 }, ouvertes: 3 } },
    ],
  },
  {
    code: 'CBS_BANK_CEMAC', name: 'Core Banking CEMAC', systemType: 'CBS', environment: 'PROD',
    version: '4.8', active: true, scoreMoyen: 84.2,
    modules: [
      { module: 'HABILITATION', totalSessions: 3, scoreMoyen: 72.5, derniereSession: '09/07/2026 10:05',
        anomalies: { bySeverity: { CRITICAL: 1, MAJOR: 2, MINOR: 4 }, byCategory: { SECURITY: 5, COMPLIANCE: 2 }, ouvertes: 3 } },
      { module: 'VIREMENTS', totalSessions: 4, scoreMoyen: 91.3, derniereSession: '09/07/2026 16:33',
        anomalies: { bySeverity: { CRITICAL: 0, MAJOR: 1, MINOR: 2 }, byCategory: { COMPLIANCE: 2, PERFORMANCE: 1 }, ouvertes: 1 } },
      { module: 'CLIENTS', totalSessions: 3, scoreMoyen: 88.6, derniereSession: '04/07/2026 09:40',
        anomalies: { bySeverity: { CRITICAL: 0, MAJOR: 1, MINOR: 3 }, byCategory: { QUALITY: 3, CONSISTENCY: 1 }, ouvertes: 1 } },
    ],
  },
  {
    code: 'AML_TRACKER', name: 'Anti-Blanchiment Tracker', systemType: 'AML', environment: 'UAT',
    version: '2.1', active: true, scoreMoyen: 66.8,
    modules: [
      { module: 'CLIENTS', totalSessions: 2, scoreMoyen: 64.0, derniereSession: '10/07/2026 10:26',
        anomalies: { bySeverity: { CRITICAL: 3, MAJOR: 2, MINOR: 1 }, byCategory: { COMPLIANCE: 4, SECURITY: 2 }, ouvertes: 5 } },
      { module: 'VIREMENTS', totalSessions: 2, scoreMoyen: 69.5, derniereSession: '10/07/2026 11:02',
        anomalies: { bySeverity: { CRITICAL: 2, MAJOR: 3, MINOR: 2 }, byCategory: { COMPLIANCE: 5, SECURITY: 2 }, ouvertes: 6 } },
    ],
  },
];

// ---- Utilitaires d'agrégation (mêmes règles que l'écran réel) ---------------------
const totauxModule = (mod) => {
  const sev = mod.anomalies.bySeverity;
  return (sev.CRITICAL || 0) + (sev.MAJOR || 0) + (sev.MINOR || 0);
};
const totauxDomaine = (dom) => ({
  sessions: dom.modules.reduce((somme, m) => somme + m.totalSessions, 0),
  anomalies: dom.modules.reduce((somme, m) => somme + totauxModule(m), 0),
  critiques: dom.modules.reduce((somme, m) => somme + (m.anomalies.bySeverity.CRITICAL || 0), 0),
  ouvertes: dom.modules.reduce((somme, m) => somme + (m.anomalies.ouvertes || 0), 0),
});

// ---- Sous-composants « maison » ------------------------------------------------------
// Mini-jauge circulaire (conic-gradient, équivalent du MiniScoreRing SVG de l'écran réel).
const MiniRing = ({ score, size = 40 }) => {
  const color = scoreColor(score);
  return (
    <div className="relative inline-flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      <div className="rounded-full" style={{ width: size, height: size, background: `conic-gradient(${color} ${(score || 0) * 3.6}deg, rgba(156,163,175,0.25) 0deg)` }} />
      <div className="absolute rounded-full bg-white dark:bg-gray-800 flex items-center justify-center" style={{ width: size - 8, height: size - 8 }}>
        <span className="text-[11px] font-bold" style={{ color }}>{score == null ? '—' : Math.round(score)}</span>
      </div>
    </div>
  );
};

// Pastilles d'anomalies par sévérité (réplique de SeverityDots).
const SeverityDots = ({ bySeverity }) => {
  const c = bySeverity.CRITICAL || 0; const m = bySeverity.MAJOR || 0; const n = bySeverity.MINOR || 0;
  if (c === 0 && m === 0 && n === 0) {
    return <span className="text-emerald-500 flex items-center gap-0.5 text-[10px] font-medium"><CheckCircle className="h-2.5 w-2.5" />0</span>;
  }
  return (
    <div className="flex items-center gap-2 text-[10px] font-medium">
      {c > 0 && <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /><span className="text-red-600 dark:text-red-400">{c}</span></span>}
      {m > 0 && <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /><span className="text-amber-600 dark:text-amber-400">{m}</span></span>}
      {n > 0 && <span className="flex items-center gap-0.5"><span className="w-2 h-2 rounded-full bg-blue-400 inline-block" /><span className="text-blue-500 dark:text-blue-400">{n}</span></span>}
    </div>
  );
};

// Mini-barres par catégorie de contrôle (réplique de CategoryBars : SEC/CMP/COH/PRF/QUA).
const CategoryBars = ({ byCategory }) => {
  const maxVal = Math.max(1, ...Object.values(byCategory || {}));
  return (
    <div className="flex items-end gap-1 h-6">
      {Object.entries(CATEGORIES).map(([cat, cfg]) => {
        const val = (byCategory && byCategory[cat]) || 0;
        const hauteur = val > 0 ? Math.max(4, (val / maxVal) * 16) : 2;
        return (
          <div key={cat} className="flex flex-col items-center" title={`${cfg.label} : ${val} anomalie(s)`}>
            <div className={`w-3 rounded-t-sm ${val > 0 ? cfg.bg : 'bg-gray-300 dark:bg-gray-600'}`} style={{ height: `${hauteur}px`, opacity: val > 0 ? 1 : 0.4 }} />
            <span className="text-[7px] text-gray-400 leading-none mt-0.5">{cfg.short}</span>
          </div>
        );
      })}
    </div>
  );
};

// Barre horizontale « maison » pour le panneau de détail.
const Bar = ({ label, value, max, color }) => (
  <div>
    <div className="flex items-center justify-between text-xs mb-1">
      <span className="text-gray-600 dark:text-gray-300">{label}</span>
      <span className="font-semibold text-gray-800 dark:text-gray-100">{value}</span>
    </div>
    <div className="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${max > 0 ? Math.min(100, (value / max) * 100) : 0}%`, backgroundColor: color }} />
    </div>
  </div>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page ------------------------------------------------------------------------------
const SupervisionPOC = () => {
  const [search, setSearch] = useState('');
  const [envFilter, setEnvFilter] = useState('ALL');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selection, setSelection] = useState(null); // { domaine, mod } = drill-down module
  const [toast, setToast] = useState('');

  const notify = (msg) => { setToast(msg); window.setTimeout(() => setToast(''), 2400); };

  // Filtrage identique à l'écran réel : environnement + recherche (nom, code, modules)
  const filtered = SUPERVISION.filter((d) => {
    const okEnv = envFilter === 'ALL' || d.environment === envFilter;
    const terme = search.trim().toLowerCase();
    const okSearch = !terme
      || d.name.toLowerCase().includes(terme)
      || d.code.toLowerCase().includes(terme)
      || d.modules.some((m) => m.module.toLowerCase().includes(terme));
    return okEnv && okSearch;
  });

  // KPIs globaux (mêmes règles que l'écran réel : moyenne des scores de domaines)
  const globaux = (() => {
    const totaux = SUPERVISION.map(totauxDomaine);
    const scores = SUPERVISION.filter((d) => d.scoreMoyen != null);
    return {
      apps: SUPERVISION.length,
      sessions: totaux.reduce((somme, t) => somme + t.sessions, 0),
      ouvertes: totaux.reduce((somme, t) => somme + t.ouvertes, 0),
      scoreGlobal: scores.length > 0 ? scores.reduce((somme, d) => somme + d.scoreMoyen, 0) / scores.length : null,
    };
  })();

  const environnements = ['ALL', ...Array.from(new Set(SUPERVISION.map((d) => d.environment))).sort()];

  const openModule = (domaine, mod) => { setSelection({ domaine, mod }); };

  return (
    <div className="space-y-8">
      {/* ===== (1) Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Supervision des Applications</h1>
          <Badge color="#10B981">Module Rapports &amp; Supervision</Badge>
          <Badge color="#3B82F6">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran <strong>Supervision</strong> (vue NOC) : matrice des domaines
          actifs × modules CBS avec scores, anomalies et drill-down par module. L'écran réel dispose
          les nœuds sur un canevas SVG avec pan/zoom ; le POC les restitue en flux vertical.
        </p>
      </div>

      {/* ===== (2) Aperçu interactif ===== */}
      <Card className="overflow-hidden">
        {/* Barre sombre de supervision (réplique du bandeau NOC) */}
        <div className="px-4 py-3 bg-gradient-to-r from-slate-800 to-slate-900 border-b border-slate-700/50">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2 bg-blue-500/20 rounded-lg shrink-0"><Monitor className="h-5 w-5 text-blue-400" /></div>
              <div className="min-w-0">
                <h2 className="text-sm font-bold text-white leading-tight">Supervision des Applications</h2>
                <p className="text-[10px] text-slate-400">Visualisation en temps réel — agrégat reports/supervision</p>
              </div>
              {/* KPIs globaux inline */}
              <div className="hidden md:flex items-center gap-2 ml-3">
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-700/50 rounded-lg text-xs text-white"><Server className="h-3.5 w-3.5 text-blue-400" /><strong>{globaux.apps}</strong><span className="text-slate-400 text-[10px]">apps</span></span>
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-700/50 rounded-lg text-xs text-white"><Activity className="h-3.5 w-3.5 text-emerald-400" /><strong>{globaux.sessions}</strong><span className="text-slate-400 text-[10px]">sessions</span></span>
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-700/50 rounded-lg text-xs"><AlertTriangle className={`h-3.5 w-3.5 ${globaux.ouvertes > 0 ? 'text-red-400' : 'text-emerald-400'}`} /><strong className={globaux.ouvertes > 0 ? 'text-red-300' : 'text-white'}>{globaux.ouvertes}</strong><span className="text-slate-400 text-[10px]">ouvertes</span></span>
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-700/50 rounded-lg text-xs"><Award className="h-3.5 w-3.5" style={{ color: scoreColor(globaux.scoreGlobal) }} /><strong style={{ color: scoreColor(globaux.scoreGlobal) }}>{Math.round(globaux.scoreGlobal)}%</strong><span className="text-slate-400 text-[10px]">global</span></span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative hidden sm:block">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher…"
                  className="w-40 pl-8 pr-3 py-1.5 text-xs bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500" />
              </div>
              <div className="relative">
                <select value={envFilter} onChange={(e) => setEnvFilter(e.target.value)}
                  className="appearance-none pl-2 pr-7 py-1.5 text-xs bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer">
                  {environnements.map((env) => <option key={env} value={env} className="bg-slate-800 text-white">{env === 'ALL' ? 'Tous env.' : env}</option>)}
                </select>
                <Filter className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400 pointer-events-none" />
              </div>
              <button onClick={() => notify('Données de supervision actualisées (simulation).')}
                className="p-1.5 bg-slate-700/50 border border-slate-600/50 rounded-lg hover:bg-slate-600/50 text-slate-300 transition-colors" title="Actualiser">
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => { setAutoRefresh(!autoRefresh); notify(autoRefresh ? 'Auto-rafraîchissement désactivé (simulation).' : 'Auto-rafraîchissement activé : 60 s, en pause si onglet masqué (simulation).'); }}
                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs border transition-colors ${autoRefresh ? 'bg-emerald-600/30 border-emerald-500/50 text-emerald-300' : 'bg-slate-700/50 border-slate-600/50 text-slate-400'}`}>
                {autoRefresh ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                {autoRefresh && <span className="font-mono text-[10px]">60s</span>}
              </button>
            </div>
          </div>
        </div>

        {/* Matrice domaines × modules */}
        <div className="p-5 space-y-5 bg-gray-50/60 dark:bg-gray-900/40">
          {filtered.length === 0 ? (
            <EmptyState icon={Eye} title="Aucune application" message="Aucune application ne correspond aux filtres. Réinitialisez la recherche ou le filtre environnement." />
          ) : filtered.map((dom) => {
            const sys = SYSTEM_TYPES[dom.systemType] || SYSTEM_TYPES.OTHER;
            const env = ENVIRONNEMENTS[dom.environment] || { label: dom.environment, color: '#6B7280' };
            const SysIcon = sys.icon;
            const totaux = totauxDomaine(dom);
            const borderColor = scoreColor(dom.scoreMoyen);
            return (
              <div key={dom.code} className="rounded-2xl border-2 overflow-hidden bg-white dark:bg-gray-800" style={{ borderColor: `${borderColor}66`, boxShadow: `0 4px 24px -6px ${borderColor}40` }}>
                {/* En-tête du nœud application */}
                <div className="p-4 flex items-start gap-4 border-b border-gray-100 dark:border-gray-700/60 flex-wrap">
                  <div className="relative shrink-0">
                    <MiniRing score={dom.scoreMoyen} size={68} />
                    {totaux.critiques > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center" title="Anomalies critiques présentes">
                        <AlertOctagon className="h-2.5 w-2.5 text-white" />
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="text-base font-bold text-gray-900 dark:text-white">{dom.name}</h3>
                      <Badge color={env.color}>{env.label}</Badge>
                      <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />Actif
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <SysIcon className="h-3.5 w-3.5" style={{ color: sys.color }} />
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">{dom.code}</span>
                      <span className="text-[10px] text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">v{dom.version}</span>
                      <Badge color={sys.color}>{sys.label}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-gray-600 dark:text-gray-300 flex-wrap">
                      <span className="flex items-center gap-1"><Activity className="h-3 w-3 text-blue-500" /><strong>{totaux.sessions}</strong> sessions</span>
                      <span className="text-gray-300 dark:text-gray-600">|</span>
                      <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-500" /><strong>{totaux.anomalies}</strong> ano
                        {totaux.ouvertes > 0 && <span className="text-red-500 font-semibold">({totaux.ouvertes} ouv.)</span>}
                      </span>
                      <span className="text-gray-300 dark:text-gray-600">|</span>
                      <span className="flex items-center gap-1"><AlertOctagon className="h-3 w-3 text-red-500" /><strong>{totaux.critiques}</strong> critiques</span>
                    </div>
                  </div>
                  <button onClick={() => notify(`Navigation vers le Tableau de Bord Domaine de ${dom.code} (simulation).`)}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shrink-0" title="Voir le tableau de bord du domaine">
                    <BarChart3 className="text-gray-400 hover:text-blue-500" style={{ width: 18, height: 18 }} />
                  </button>
                </div>

                {/* Grille des cartes modules */}
                <div className="p-3 flex flex-wrap gap-2">
                  {dom.modules.map((mod) => {
                    const isSelected = selection && selection.domaine.code === dom.code && selection.mod.module === mod.module;
                    return (
                      <div key={mod.module} onClick={() => openModule(dom, mod)}
                        className={`rounded-xl border p-2.5 cursor-pointer transition-all hover:shadow-md bg-white dark:bg-gray-800/80 w-[170px] ${
                          isSelected ? 'ring-2 ring-blue-500 border-blue-300' :
                          mod.scoreMoyen < 70 ? 'border-red-300 dark:border-red-700' :
                          mod.scoreMoyen < 85 ? 'border-amber-300 dark:border-amber-700' :
                          'border-emerald-200 dark:border-emerald-800'}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <MiniRing score={mod.scoreMoyen} size={34} />
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-bold text-gray-800 dark:text-gray-100 truncate leading-tight">{mod.module}</p>
                            <p className="text-[9px] text-gray-500 dark:text-gray-400">{mod.totalSessions} session{mod.totalSessions > 1 ? 's' : ''}</p>
                          </div>
                        </div>
                        <p className={`text-[9px] mb-1.5 ${mod.anomalies.ouvertes > 0 ? 'text-red-500 font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
                          {mod.anomalies.ouvertes > 0 ? `${mod.anomalies.ouvertes} anomalie(s) ouverte(s)` : 'Aucune anomalie ouverte'}
                        </p>
                        <div className="mb-1.5"><SeverityDots bySeverity={mod.anomalies.bySeverity} /></div>
                        <CategoryBars byCategory={mod.anomalies.byCategory} />
                        <p className="text-[8px] text-gray-400 mt-1 flex items-center gap-0.5"><Clock className="h-2 w-2" />{mod.derniereSession}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Drill-down : panneau de détail du module sélectionné */}
          {selection && (
            <div className="rounded-2xl border-2 border-blue-300 dark:border-blue-500/50 bg-white dark:bg-gray-800 p-5">
              <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <MiniRing score={selection.mod.scoreMoyen} size={56} />
                  <div>
                    <h4 className="text-base font-bold text-gray-900 dark:text-white">Module {selection.mod.module}</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {selection.domaine.name} <span className="font-mono">({selection.domaine.code})</span> • score moyen{' '}
                      <strong style={{ color: scoreColor(selection.mod.scoreMoyen) }}>{selection.mod.scoreMoyen}% — {scoreLevel(selection.mod.scoreMoyen)}</strong>
                    </p>
                  </div>
                </div>
                <button onClick={() => setSelection(null)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Fermer le détail">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700/60">
                  <h5 className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Activité</h5>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">Sessions d'audit</span><span className="font-bold text-gray-900 dark:text-white">{selection.mod.totalSessions}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">Dernière session</span><span className="font-semibold text-gray-900 dark:text-white text-xs">{selection.mod.derniereSession}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">Anomalies ouvertes</span><span className={`font-bold ${selection.mod.anomalies.ouvertes > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600'}`}>{selection.mod.anomalies.ouvertes}</span></div>
                    <div className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">Anomalies totales</span><span className="font-bold text-gray-900 dark:text-white">{totauxModule(selection.mod)}</span></div>
                  </div>
                  <button onClick={() => notify(`Navigation vers les sessions du module ${selection.mod.module} du domaine ${selection.domaine.code} (simulation).`)}
                    className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium transition-colors">
                    Voir les sessions <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700/60">
                  <h5 className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Anomalies par sévérité</h5>
                  <div className="space-y-3">
                    {Object.entries(SEVERITES).map(([k, c]) => (
                      <Bar key={k} label={c.label} value={selection.mod.anomalies.bySeverity[k] || 0}
                        max={Math.max(1, ...Object.values(selection.mod.anomalies.bySeverity))} color={c.color} />
                    ))}
                  </div>
                </div>
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700/60">
                  <h5 className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Anomalies par catégorie de contrôle</h5>
                  <div className="space-y-3">
                    {Object.entries(CATEGORIES).map(([k, c]) => (
                      <Bar key={k} label={`${c.label} (${c.short})`} value={selection.mod.anomalies.byCategory[k] || 0}
                        max={Math.max(1, ...Object.values(selection.mod.anomalies.byCategory))} color={c.color} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Légende des seuils et des pastilles */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2"><Info className="h-3.5 w-3.5" />Légende</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div className="space-y-1.5">
                <p className="font-semibold text-gray-700 dark:text-gray-200">Seuils de score</p>
                <p className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-emerald-500" />ACCEPTABLE : score ≥ 85</p>
                <p className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-amber-500" />ALERTE : 70 ≤ score &lt; 85</p>
                <p className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-red-500" />CRITIQUE : score &lt; 70</p>
              </div>
              <div className="space-y-1.5">
                <p className="font-semibold text-gray-700 dark:text-gray-200">Pastilles de sévérité</p>
                <p className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-500" />CRITICAL (poids ×10)</p>
                <p className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500" />MAJOR (poids ×5)</p>
                <p className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-blue-400" />MINOR (poids ×1)</p>
              </div>
              <div className="space-y-1.5">
                <p className="font-semibold text-gray-700 dark:text-gray-200">Catégories de contrôle</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(CATEGORIES).map(([k, c]) => <Badge key={k} color={c.color}>{c.short} — {c.label}</Badge>)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Toast simulé */}
        {toast && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <Info className="h-4 w-4 text-blue-500" />{toast}
          </div>
        )}
      </Card>

      {/* ===== (3) Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Monitor className="h-5 w-5 text-blue-500" />1. Lire la matrice</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Chaque <strong>nœud</strong> représente un domaine d'audit ACTIF : jauge de score, environnement, type système et KPIs (sessions, anomalies, critiques).</GuideStep>
              <GuideStep n={2}>La <strong>bordure</strong> du nœud et un point rouge pulsant signalent les domaines à risque (score bas, critiques présentes).</GuideStep>
              <GuideStep n={3}>Les KPIs de la barre sombre agrègent l'ensemble : apps, sessions, anomalies ouvertes, score global.</GuideStep>
            </ol>
          </CardContent></Card>
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Layers className="h-5 w-5 text-blue-500" />2. Analyser un module</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Chaque carte module montre sa <strong>mini-jauge</strong>, ses <strong>pastilles de sévérité</strong> et ses <strong>mini-barres par catégorie</strong> (SEC/CMP/COH/PRF/QUA).</GuideStep>
              <GuideStep n={2}>Cliquez sur une carte pour ouvrir le <strong>drill-down</strong> : répartitions détaillées et anomalies ouvertes. <button onClick={() => openModule(SUPERVISION[0], SUPERVISION[0].modules[0])} className="text-blue-600 dark:text-blue-400 underline">Essayer (HABILITATION)</button></GuideStep>
              <GuideStep n={3}>Le bouton <strong>Voir les sessions</strong> ouvre le Suivi d'audit filtré sur le domaine + module.</GuideStep>
            </ol>
          </CardContent></Card>
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Filter className="h-5 w-5 text-blue-500" />3. Filtrer &amp; surveiller</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>La <strong>recherche</strong> porte sur le nom, le code et les modules ; le filtre <strong>environnement</strong> restreint aux PROD/UAT…</GuideStep>
              <GuideStep n={2}>L'<strong>auto-rafraîchissement</strong> (opt-in, 60 s) recharge l'agrégat, en pause si l'onglet est masqué.</GuideStep>
              <GuideStep n={3}>Dans l'écran réel, la molette <strong>zoome</strong> et le glisser <strong>déplace</strong> le canevas (contrôles en bas à droite).</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== (4) Règles de gestion ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Calculator className="h-5 w-5 text-blue-500" />Règles de gestion</h3>
        <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 mb-4">
          <p className="text-sm font-mono text-gray-800 dark:text-gray-100">Score = 100 − (CRITICAL×10 + MAJOR×5 + MINOR×1) ÷ NbContrôles × 10</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Seuils : <strong style={{ color: '#EF4444' }}>CRITIQUE &lt; 70</strong> • <strong style={{ color: '#F59E0B' }}>ALERTE &lt; 85</strong> • <strong style={{ color: '#10B981' }}>ACCEPTABLE ≥ 85</strong>.
            Score d'un module = moyenne des scores de ses sessions ; score d'un domaine = moyenne sur le domaine ;
            score global = moyenne des scores des domaines supervisés.
          </p>
        </div>
        <ul className="space-y-1.5 text-sm text-gray-600 dark:text-gray-300 list-disc list-inside">
          <li>UN SEUL appel d'agrégat (<code>reports/supervision</code>) alimente toute la matrice — aucune table intégrale (sessions, anomalies) n'est téléchargée côté client.</li>
          <li>Seuls les domaines <strong>actifs</strong> sont supervisés ; les totaux d'un domaine sont la somme des agrégats de ses modules.</li>
          <li>Les compteurs de contrôles ok/total ne sont pas exposés par l'agrégat : la carte module affiche à la place le compteur d'<strong>anomalies ouvertes</strong>.</li>
          <li>Les 8 modules CBS audités : HABILITATION, COMPTABILITE, CREDITS, DEPOTS, VIREMENTS, CLIENTS, TRESORERIE, RISQUES.</li>
        </ul>
      </CardContent></Card>

      {/* ===== (5) Endpoints ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><ListChecks className="h-5 w-5 text-blue-500" />Endpoints API</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Méthode', 'Endpoint', 'Usage', 'Permission'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {[
                ['/api/scanbank/reports/supervision', 'Agrégat unique : domaines actifs × modules (sessions, scores, anomalies bySeverity/byCategory/ouvertes, dernière session)', 'SCANBANK_REPORT_VIEW'],
                ['/api/scanbank/reports/domains/{code}/dashboard', 'Navigation vers le tableau de bord d’un domaine', 'SCANBANK_REPORT_VIEW'],
                ['/api/scanbank/audit/sessions/page?domainCode={code}&module={module}', 'Drill-down : sessions du module', 'SCANBANK_AUDIT_VIEW'],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2"><Badge color="#10B981">GET</Badge></td>
                  <td className="px-4 py-2 font-mono text-xs break-all">{r[0]}</td>
                  <td className="px-4 py-2 text-xs">{r[1]}</td>
                  <td className="px-4 py-2 font-mono text-[11px]">{r[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
          <ArrowRight className="h-3 w-3" />Source : écran réel <code>[RPT][03]-Supervision.js</code> + ReportController (scanbank_core).
        </p>
      </CardContent></Card>
    </div>
  );
};

export default SupervisionPOC;
